if (!customElements.get('interactive-collage')) {
  customElements.define(
    'interactive-collage',
    class InteractiveCollage extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.canvas = this.querySelector('[data-collage-canvas]');
        this.stickers = Array.from(this.querySelectorAll('[data-collage-sticker]'));
        this.resetButton = this.querySelector('[data-collage-reset]');
        this.active = null;
        this.dragFrame = null;
        this.resizeFrame = null;
        this.nextLayer = Math.max(0, ...this.stickers.map((sticker) => Number(sticker.dataset.layer))) + 1;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerEnd = this.onPointerEnd.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onResize = this.onResize.bind(this);
        this.reset = this.reset.bind(this);

        this.stickers.forEach((sticker) => {
          sticker.addEventListener('pointerdown', this.onPointerDown);
          sticker.addEventListener('keydown', this.onKeyDown);
          sticker.addEventListener('focus', this.onFocus);
          sticker.addEventListener('dragstart', this.preventDefault);
        });

        this.resetButton?.addEventListener('click', this.reset);
        window.addEventListener('resize', this.onResize, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.canvas);
        }
      }

      disconnectedCallback() {
        this.stickers?.forEach((sticker) => {
          sticker.removeEventListener('pointerdown', this.onPointerDown);
          sticker.removeEventListener('keydown', this.onKeyDown);
          sticker.removeEventListener('focus', this.onFocus);
          sticker.removeEventListener('dragstart', this.preventDefault);
        });

        this.resetButton?.removeEventListener('click', this.reset);
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver?.disconnect();
        if (this.dragFrame) cancelAnimationFrame(this.dragFrame);
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
        this.initialized = false;
      }

      preventDefault(event) {
        event.preventDefault();
      }

      bringForward(sticker) {
        if (this.nextLayer >= 90) {
          this.stickers
            .slice()
            .sort((a, b) => Number(getComputedStyle(a).zIndex) - Number(getComputedStyle(b).zIndex))
            .forEach((item, index) => item.style.setProperty('--sticker-layer', index + 1));
          this.nextLayer = this.stickers.length + 1;
        }

        sticker.style.setProperty('--sticker-layer', this.nextLayer++);
      }

      getCenter(sticker) {
        const canvasRect = this.canvas.getBoundingClientRect();
        const stickerRect = sticker.getBoundingClientRect();
        return {
          x: stickerRect.left - canvasRect.left + stickerRect.width / 2,
          y: stickerRect.top - canvasRect.top + stickerRect.height / 2
        };
      }

      clamp(sticker, x, y) {
        const canvasRect = this.canvas.getBoundingClientRect();
        const stickerRect = sticker.getBoundingClientRect();
        const marginX = canvasRect.width * 0.05;
        const marginY = canvasRect.height * 0.05;
        const halfWidth = Math.min(stickerRect.width / 2, canvasRect.width * 0.45);
        const halfHeight = Math.min(stickerRect.height / 2, canvasRect.height * 0.45);
        const minX = Math.max(marginX, halfWidth);
        const maxX = Math.max(minX, Math.min(canvasRect.width - marginX, canvasRect.width - halfWidth));
        const minY = Math.max(marginY, halfHeight);
        const maxY = Math.max(minY, Math.min(canvasRect.height - marginY, canvasRect.height - halfHeight));

        return {
          x: Math.min(Math.max(x, minX), maxX),
          y: Math.min(Math.max(y, minY), maxY)
        };
      }

      place(sticker, x, y) {
        const point = this.clamp(sticker, x, y);
        sticker.style.left = `${point.x}px`;
        sticker.style.top = `${point.y}px`;
      }

      onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;

        const sticker = event.currentTarget;
        const canvasRect = this.canvas.getBoundingClientRect();
        const center = this.getCenter(sticker);
        this.bringForward(sticker);
        sticker.classList.add('is-dragging');
        sticker.setPointerCapture(event.pointerId);
        this.active = {
          sticker,
          pointerId: event.pointerId,
          offsetX: event.clientX - canvasRect.left - center.x,
          offsetY: event.clientY - canvasRect.top - center.y,
          clientX: event.clientX,
          clientY: event.clientY
        };

        sticker.addEventListener('pointermove', this.onPointerMove);
        sticker.addEventListener('pointerup', this.onPointerEnd);
        sticker.addEventListener('pointercancel', this.onPointerEnd);
        event.preventDefault();
      }

      onPointerMove(event) {
        if (!this.active || event.pointerId !== this.active.pointerId) return;

        this.active.clientX = event.clientX;
        this.active.clientY = event.clientY;
        if (this.dragFrame) return;

        this.dragFrame = requestAnimationFrame(() => {
          this.dragFrame = null;
          if (!this.active) return;
          const canvasRect = this.canvas.getBoundingClientRect();
          this.active.sticker.dataset.moved = 'true';
          this.place(
            this.active.sticker,
            this.active.clientX - canvasRect.left - this.active.offsetX,
            this.active.clientY - canvasRect.top - this.active.offsetY
          );
        });
        event.preventDefault();
      }

      onPointerEnd(event) {
        if (!this.active || event.pointerId !== this.active.pointerId) return;

        const sticker = this.active.sticker;
        sticker.classList.remove('is-dragging');
        sticker.removeEventListener('pointermove', this.onPointerMove);
        sticker.removeEventListener('pointerup', this.onPointerEnd);
        sticker.removeEventListener('pointercancel', this.onPointerEnd);
        if (sticker.hasPointerCapture(event.pointerId)) sticker.releasePointerCapture(event.pointerId);
        this.active = null;
      }

      onFocus(event) {
        this.bringForward(event.currentTarget);
      }

      onKeyDown(event) {
        const directions = {
          ArrowLeft: [-1, 0],
          ArrowRight: [1, 0],
          ArrowUp: [0, -1],
          ArrowDown: [0, 1]
        };
        if (!directions[event.key]) return;

        const sticker = event.currentTarget;
        const center = this.getCenter(sticker);
        const step = event.shiftKey ? 2 : 10;
        sticker.dataset.moved = 'true';
        this.place(sticker, center.x + directions[event.key][0] * step, center.y + directions[event.key][1] * step);
        event.preventDefault();
      }

      onResize() {
        if (this.resizeFrame) return;

        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.stickers.forEach((sticker) => {
            if (sticker.dataset.moved !== 'true') {
              sticker.style.left = `${sticker.dataset.left}%`;
              sticker.style.top = `${sticker.dataset.top}%`;
            }
            const center = this.getCenter(sticker);
            this.place(sticker, center.x, center.y);
          });
        });
      }

      reset() {
        if (this.active) this.onPointerEnd({ pointerId: this.active.pointerId });

        this.stickers.forEach((sticker) => {
          sticker.dataset.moved = 'false';
          sticker.style.left = `${sticker.dataset.left}%`;
          sticker.style.top = `${sticker.dataset.top}%`;
          sticker.style.setProperty('--sticker-rotation', `${sticker.dataset.rotation}deg`);
          sticker.style.removeProperty('--sticker-width');
          sticker.style.setProperty('--sticker-layer', sticker.dataset.layer);
        });
        this.nextLayer = Math.max(0, ...this.stickers.map((sticker) => Number(sticker.dataset.layer))) + 1;
      }
    }
  );
}
